import { useEffect, useRef, useCallback } from "react"

interface UseInfiniteScrollOptions {
  hasMore: boolean
  isLoading: boolean
  onLoadMore: () => void
  rootMargin?: string
  threshold?: number
}

/**
 * Custom hook for implementing infinite scroll with Intersection Observer API
 *
 * Best practices implemented:
 * - Uses Intersection Observer for better performance than scroll listeners
 * - Prevents multiple simultaneous load requests
 * - Cleans up observer on unmount
 * - Configurable root margin and threshold for trigger distance
 * - Respects hasMore flag to prevent unnecessary requests
 */
export const useInfiniteScroll = ({
  hasMore,
  isLoading,
  onLoadMore,
  rootMargin = "100px",
  threshold = 0.1
}: UseInfiniteScrollOptions) => {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries
      if (target.isIntersecting && hasMore && !isLoading) {
        onLoadMore()
      }
    },
    [hasMore, isLoading, onLoadMore]
  )

  useEffect(() => {
    const element = loadMoreRef.current
    if (!element) return

    // Create observer with options
    const option = {
      root: null, // viewport
      rootMargin,
      threshold
    }

    observerRef.current = new IntersectionObserver(handleObserver, option)
    observerRef.current.observe(element)

    // Cleanup
    return () => {
      if (observerRef.current && element) {
        observerRef.current.unobserve(element)
        observerRef.current.disconnect()
      }
    }
  }, [handleObserver, rootMargin, threshold])

  return loadMoreRef
}
